import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useModals } from "../../../../context/ModalContext";
import { ClientDetailsTabs } from "../../../clients/ClientDetailsModal/ClientDetailsTabs";
import { BaseModalComponent } from "../../../common/BaseModalComponent";
import { WorkforceDetailTabs } from "../../../employees/components/WorkforceDetailTabs";
import { VendorForm } from "../../../vendors/VendorForm";
import { useVirtualAssistant } from "../../context/VirtualAssistantContext";
import { ContactDetails } from "./ContactDetails";

const getContactDisplayName = (contact: any, fallback: string) => {
  const name = [contact?.name, contact?.lastName].filter(Boolean).join(" ").trim();
  return name || contact?.firstName || contact?.fullName || fallback;
};

const getWorkforceModalLabel = (contact: any, fallback: string) => `Workforce — ${getContactDisplayName(contact, fallback)}`;

export function VirtualReceptionistContactDetailsHost() {
  const { contactToEdit, quickSavePhone, contactMode, closeContactDetails } = useVirtualAssistant();
  const { openModal, closeByKey } = useModals();
  const companyId = useSelector((state: any) => state.userState.company?.id);

  const rawType = ((contactToEdit as any)?.type || (contactToEdit as any)?.role || (contactToEdit as any)?.contactType || "").toLowerCase();

  const isResolvedClient = contactToEdit && (rawType.includes("client") || rawType.includes("prospect") || !!(contactToEdit as any).clientUid);

  const isResolvedWorkforce =
    contactToEdit &&
    (rawType.includes("workforce") ||
      rawType.includes("employee") ||
      rawType.includes("supervisor") ||
      rawType.includes("contractor") ||
      rawType.includes("applicant") ||
      !!(contactToEdit as any).workforceUid);

  const isResolvedVendor = contactToEdit && (rawType.includes("vendor") || rawType.includes("supplier") || !!(contactToEdit as any).vendorUid);

  const handleClose = () => {
    closeContactDetails();
  };

  useEffect(() => {
    if (!companyId) return;

    if (quickSavePhone || contactToEdit) {
      if (isResolvedClient && contactToEdit) {
        const clientName = [contactToEdit.name, contactToEdit.lastName].filter(Boolean).join(" ") || contactToEdit.name || "Client";
        const clientUid = (contactToEdit as any).clientUid || contactToEdit.uid;
        openModal({ key: `update-client-${clientUid}`, preventParentTrigger: true }, <ClientDetailsTabs clientUid={clientUid} />);
      } else if (isResolvedWorkforce && contactToEdit) {
        const workforceUid = (contactToEdit as any).workforceUid || contactToEdit.uid;
        openModal(
          {
            key: `Workforce-Portfolio-Details-${workforceUid}`,
            title: getWorkforceModalLabel(contactToEdit, `Workforce ${String(workforceUid).slice(0, 12)}`),
            preventParentTrigger: true,
          },
          <WorkforceDetailTabs workforceUid={workforceUid} />,
        );
      } else if (isResolvedVendor && contactToEdit) {
        const vendorUid = (contactToEdit as any).vendorUid || contactToEdit.uid;
        openModal(
          { key: `update-vendor-${vendorUid}`, preventParentTrigger: true },
          <VendorForm mode="update" vendorUid={vendorUid} onUpdate={async () => handleClose()} />,
        );
      } else {
        const content = (
          <ContactDetails
            phoneNumber={quickSavePhone || ""}
            companyId={Number(companyId)}
            contact={contactToEdit}
            initialMode={contactMode as "create" | "link"}
            onClose={() => {
              closeByKey("Virtual-Receptionist-Contact-Details-Modal");
              handleClose();
            }}
          />
        );
        openModal({ key: "Virtual-Receptionist-Contact-Details-Modal", preventParentTrigger: true }, content);
      }
    }
  }, [quickSavePhone, contactToEdit, contactMode, companyId, isResolvedClient, isResolvedWorkforce, isResolvedVendor]);

  if (!companyId) return null;

  return (
    <>
      {isResolvedClient && contactToEdit ? (
        <BaseModalComponent
          modalKey={`update-client-${(contactToEdit as any).clientUid || contactToEdit.uid}`}
          modalLabel={`Update ${[contactToEdit.name, contactToEdit.lastName].filter(Boolean).join(" ") || "Client"}`}
          onClose={handleClose}
          defaultSizes={{ width: 700 }}
        >
          <ClientDetailsTabs clientUid={(contactToEdit as any).clientUid || contactToEdit.uid} />
        </BaseModalComponent>
      ) : isResolvedWorkforce && contactToEdit ? (
        <BaseModalComponent
          modalKey={`Workforce-Portfolio-Details-${(contactToEdit as any).workforceUid || contactToEdit.uid}`}
          modalLabel={getWorkforceModalLabel(
            contactToEdit,
            `Workforce ${String((contactToEdit as any).workforceUid || contactToEdit.uid).slice(0, 12)}`,
          )}
          onClose={handleClose}
          defaultSizes={{ width: 800 }}
          // @ts-ignore
          sx={{ zIndex: 99999 }}
        >
          <WorkforceDetailTabs workforceUid={(contactToEdit as any).workforceUid || contactToEdit.uid} />
        </BaseModalComponent>
      ) : isResolvedVendor && contactToEdit ? (
        <BaseModalComponent
          modalKey={`update-vendor-${(contactToEdit as any).vendorUid || contactToEdit.uid}`}
          modalLabel="Update Vendor"
          onClose={handleClose}
          defaultSizes={{ width: 800 }}
        >
          <VendorForm mode="update" vendorUid={(contactToEdit as any).vendorUid || contactToEdit.uid} onUpdate={async () => handleClose()} />
        </BaseModalComponent>
      ) : (
        <BaseModalComponent
          modalKey="Virtual-Receptionist-Contact-Details-Modal"
          modalLabel={contactToEdit ? "Edit Contact" : "Save or Link Contact"}
          onClose={handleClose}
          defaultSizes={{ width: 500, height: "auto" }}
        >
          {quickSavePhone ? (
            <ContactDetails
              phoneNumber={quickSavePhone}
              companyId={Number(companyId)}
              contact={contactToEdit}
              initialMode={contactMode as "create" | "link"}
              onClose={() => {
                closeByKey("Virtual-Receptionist-Contact-Details-Modal");
                handleClose();
              }}
            />
          ) : null}
        </BaseModalComponent>
      )}
    </>
  );
}
